import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useSweepParams } from '../navigation-utils/useSweepParams';
import useSweepNavigate from '../navigation-utils/useSweepNavigate';
import { removeOrgFromPath, replaceOrgInPath } from '../navigation-utils/urlOrgUtils';
import {
  selectConnectedCrmOrgs,
  selectCrmOrgsAreReady,
} from '../components/pages/environments/environmentsReducer';
import { selectDefaultCreationCrmOrgId, selectUserAccountId } from '../reducers/userInfoReducer';
import useUserInfo from './useUserInfo';
import { readableUUID } from '../navigation-utils/readableUUID';
import { useAccountSelectionContext } from '../components/account-selection/AccountSelectionContext';

/**
 * This hook syncs state to match URL changes. It's the centralized point
 * for ensuring state always reflects what the URL shows.
 *
 * Responsibilities:
 * - Wait for heuristic completion (crmOrgsAreReady)
 * - Wait for account validation to complete (isAccountReady from AccountSelectionContext)
 * - Detect invalid orgs in URL and replace with correct org (single redirect)
 * - Sync state (preferences, axios headers, redux) when URL has valid org
 *
 */
const useCrmOrgFromUrl = () => {
  const { fullCrmOrgId, crmOrgId: readableCrmOrgId } = useSweepParams<{ crmOrgId: string }>(); // Full UUID for business logic
  const crmOrgIdState = useSelector(selectDefaultCreationCrmOrgId);
  const connectedCrmOrgs = useSelector(selectConnectedCrmOrgs);
  const crmOrgsAreReady = useSelector(selectCrmOrgsAreReady);
  const userAccountId = useSelector(selectUserAccountId);
  const location = useLocation();
  const { navigate } = useSweepNavigate();
  const { updateDefaultCreationCrmOrgId } = useUserInfo();
  const { isAccountReady } = useAccountSelectionContext();

  useEffect(() => {
    // Wait for heuristic to complete and account validation to finish to avoid conflicts
    if (!crmOrgsAreReady || !userAccountId || !isAccountReady) {
      return;
    }

    if (readableCrmOrgId) {
      //invalid org (this orgId doesn't exist in this account)
      if (!fullCrmOrgId) {
        const readableCrmOrgIdState = readableUUID(crmOrgIdState ?? '');
        const isInvalidParamEqualToState =
          readableCrmOrgIdState && readableCrmOrgIdState === readableCrmOrgId;

        // Clear invalid state
        if (isInvalidParamEqualToState) {
          updateDefaultCreationCrmOrgId(null);
        }

        const firstCrmOrgId = connectedCrmOrgs.length > 0 ? connectedCrmOrgs[0].id : null;
        const correctOrgId = isInvalidParamEqualToState
          ? firstCrmOrgId
          : (crmOrgIdState ?? firstCrmOrgId);

        if (correctOrgId) {
          // Replace invalid org with correct org (single redirect)
          const routeWithCorrectOrg = replaceOrgInPath(
            location.pathname,
            readableCrmOrgId,
            correctOrgId,
            userAccountId,
          );
          navigate(routeWithCorrectOrg, { replace: true });
        } else {
          // No orgs available - remove invalid org and let WithUnhappyStates handle
          const routeWithoutOrg = removeOrgFromPath(location.pathname, readableCrmOrgId);
          navigate(routeWithoutOrg, { replace: true });
        }
      }

      // Valid org in URL - sync state to match URL
      if (fullCrmOrgId && fullCrmOrgId !== crmOrgIdState) {
        updateDefaultCreationCrmOrgId(fullCrmOrgId);
      }
    }
  }, [
    fullCrmOrgId,
    readableCrmOrgId,
    crmOrgIdState,
    connectedCrmOrgs,
    userAccountId,
    crmOrgsAreReady,
    isAccountReady,
    location.pathname,
    navigate,
    updateDefaultCreationCrmOrgId,
  ]);
};

export default useCrmOrgFromUrl;
